import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Send,
  Reply,
  Heart,
  MoreVertical,
  Pin,
  Trash2,
  Edit3,
  Flag,
  Search,
  Filter,
  Users,
  Clock,
  ChevronDown,
  ChevronUp,
  Plus,
  Lock,
  Unlock,
} from "lucide-react";
import {
  Discussion,
  DiscussionPost,
  DiscussionReply,
} from "../../types/elearning";
import apiClient from "../../services/api";
import { io, Socket } from "socket.io-client";
import { useAuth } from "../../hooks/useAuth";

interface DiscussionState {
  discussion: Discussion | null;
  posts: DiscussionPost[];
  loading: boolean;
  error: string | null;
  newPostContent: string;
  replyingTo: string | null;
  replyContent: string;
  editingPost: string | null;
  editContent: string;
  searchQuery: string;
  sortBy: "newest" | "oldest" | "popular";
  showPinnedOnly: boolean;
}

interface UserTypingIndicatorProps {
  typingUsers: string[];
}

const UserTypingIndicator: React.FC<UserTypingIndicatorProps> = ({
  typingUsers,
}) => {
  if (typingUsers.length === 0) return null;

  return (
    <div className="flex items-center space-x-2 text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
        <div
          className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
          style={{ animationDelay: "0.1s" }}
        ></div>
        <div
          className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
          style={{ animationDelay: "0.2s" }}
        ></div>
      </div>
      <span>
        {typingUsers.length === 1
          ? `${typingUsers[0]} is typing...`
          : typingUsers.length === 2
          ? `${typingUsers[0]} and ${typingUsers[1]} are typing...`
          : `${typingUsers.length} people are typing...`}
      </span>
    </div>
  );
};

interface PostComponentProps {
  post: DiscussionPost;
  onReply: (postId: string) => void;
  onLike: (postId: string) => void;
  onEdit: (postId: string, content: string) => void;
  onDelete: (postId: string) => void;
  onPin: (postId: string) => void;
  onReport: (postId: string) => void;
  currentUserId: string;
  isTeacher: boolean;
}

const PostComponent: React.FC<PostComponentProps> = ({
  post,
  onReply,
  onLike,
  onEdit,
  onDelete,
  onPin,
  onReport,
  currentUserId,
  isTeacher,
}) => {
  const [showReplies, setShowReplies] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);

  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate if current user liked this post
  const isLiked = post.likedBy?.includes(currentUserId) || false;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEdit = () => {
    if (editing) {
      onEdit(post._id, editContent);
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const formatDate = (dateInput: string | Date) => {
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return "Invalid date";

      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString();
    } catch (error) {
      return "Invalid date";
    }
  };

  const canModerate = isTeacher || post.authorId === currentUserId;

  return (
    <div
      className={`border rounded-lg p-4 ${
        post.isPinned
          ? "bg-yellow-50 border-yellow-200"
          : "bg-white border-gray-200"
      }`}
    >
      {/* Post Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-blue-800">
              {post.authorName?.charAt(0).toUpperCase() || "U"}
            </span>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900">
                {post.authorName}
              </span>
              {post.authorType === "Teacher" && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                  Teacher
                </span>
              )}
              {post.isPinned && <Pin className="w-4 h-4 text-yellow-600" />}
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Clock className="w-3 h-3" />
              <span>{formatDate(post.createdAt)}</span>
              {post.updatedAt !== post.createdAt && (
                <span className="text-xs">(edited)</span>
              )}
            </div>
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <div className="py-1">
                <button
                  onClick={() => onReply(post._id)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                >
                  <Reply className="w-4 h-4" />
                  <span>Reply</span>
                </button>

                {post.authorId === currentUserId && (
                  <button
                    onClick={handleEdit}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>{editing ? "Save" : "Edit"}</span>
                  </button>
                )}

                {isTeacher && (
                  <button
                    onClick={() => onPin(post._id)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Pin className="w-4 h-4" />
                    <span>{post.isPinned ? "Unpin" : "Pin"}</span>
                  </button>
                )}

                {canModerate && (
                  <button
                    onClick={() => onDelete(post._id)}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                )}

                {post.authorId !== currentUserId && (
                  <button
                    onClick={() => onReport(post._id)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Flag className="w-4 h-4" />
                    <span>Report</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Post Content */}
      <div className="mb-3">
        {editing ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
            <div className="flex space-x-2">
              <button
                onClick={handleEdit}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setEditContent(post.content);
                }}
                className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className="prose max-w-none text-gray-800"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        )}
      </div>

      {/* Post Actions */}
      <div className="flex items-center space-x-4 text-sm text-gray-500">
        <button
          onClick={() => onLike(post._id)}
          className={`flex items-center space-x-1 hover:text-red-600 ${
            isLiked ? "text-red-600" : ""
          }`}
        >
          <Heart className={`w-4 h-4 ${isLiked ? "fill-current" : ""}`} />
          <span>{post.likes || 0}</span>
        </button>

        <button
          onClick={() => onReply(post._id)}
          className="flex items-center space-x-1 hover:text-blue-600"
        >
          <Reply className="w-4 h-4" />
          <span>Reply</span>
        </button>

        {post.replies && post.replies.length > 0 && (
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="flex items-center space-x-1 hover:text-gray-700"
          >
            {showReplies ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span>
              {post.replies.length}{" "}
              {post.replies.length === 1 ? "reply" : "replies"}
            </span>
          </button>
        )}
      </div>

      {/* Replies */}
      {showReplies && post.replies && post.replies.length > 0 && (
        <div className="mt-4 ml-8 space-y-3">
          {post.replies.map((reply) => (
            <div key={reply._id} className="border-l-2 border-gray-200 pl-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-gray-600">
                      {reply.authorName?.charAt(0).toUpperCase() || "U"}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {reply.authorName}
                  </span>
                  {reply.authorType === "Teacher" && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full">
                      Teacher
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {formatDate(reply.createdAt)}
                  </span>
                </div>
              </div>

              <div
                className="mt-1 text-sm text-gray-800 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: reply.content }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DiscussionBoard: React.FC = () => {
  const { discussionId } = useParams<{ discussionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  const [state, setState] = useState<DiscussionState>({
    discussion: null,
    posts: [],
    loading: true,
    error: null,
    newPostContent: "",
    replyingTo: null,
    replyContent: "",
    editingPost: null,
    editContent: "",
    searchQuery: "",
    sortBy: "newest",
    showPinnedOnly: false,
  });

  // Get user data from auth context
  const currentUserId = user?.id || "";
  const isTeacher = user?.roles?.includes("teacher") || false;

  useEffect(() => {
    if (discussionId) {
      loadDiscussion();
      initializeSocket();
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [discussionId]);

  const initializeSocket = () => {
    const apiUrl = import.meta.env.VITE_BACKEND_URL;
    if (!apiUrl) {
      console.error("VITE_BACKEND_URL environment variable is not set");
      return;
    }

    const newSocket = io(apiUrl);

    newSocket.on("connect", () => {
      newSocket.emit("join-discussion", discussionId);
    });

    newSocket.on(
      "new-post",
      (data: { discussionId: string; post: DiscussionPost }) => {
        // Reload discussion to get updated posts
        if (data.discussionId === discussionId) {
          loadDiscussion();
        }
      }
    );

    newSocket.on("post-updated", (updatedPost: DiscussionPost) => {
      setState((prev) => ({
        ...prev,
        posts: prev.posts.map((post) =>
          post._id === updatedPost._id ? updatedPost : post
        ),
      }));
    });

    newSocket.on("post-deleted", (postId: string) => {
      setState((prev) => ({
        ...prev,
        posts: prev.posts.filter((post) => post._id !== postId),
      }));
    });

    newSocket.on(
      "user-typing",
      (data: { userId: string; userName: string }) => {
        setTypingUsers((prev) => {
          if (!prev.includes(data.userName)) {
            return [...prev, data.userName];
          }
          return prev;
        });

        // Remove typing indicator after 3 seconds
        setTimeout(() => {
          setTypingUsers((prev) =>
            prev.filter((user) => user !== data.userName)
          );
        }, 3000);
      }
    );

    newSocket.on("users-online", (users: string[]) => {
      setOnlineUsers(users);
    });

    setSocket(newSocket);
  };

  const loadDiscussion = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const response = await apiClient.get(
        `/elearning/discussions/${discussionId}`
      );
      const { discussion, posts } = response.data;

      setState((prev) => ({
        ...prev,
        discussion,
        posts,
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error ? error.message : "Failed to load discussion",
        loading: false,
      }));
    }
  };

  const handleTyping = useCallback(() => {
    if (socket) {
      socket.emit("user-typing", { userId: currentUserId });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (socket) {
        socket.emit("user-stopped-typing", { userId: currentUserId });
      }
    }, 1000);
  }, [socket, currentUserId]);

  const createPost = async () => {
    if (!state.newPostContent.trim()) return;

    try {
      // Create a reply to the main discussion
      await apiClient.post(`/elearning/discussions/${discussionId}/replies`, {
        content: state.newPostContent,
      });

      // Reload the discussion to get updated posts
      await loadDiscussion();

      setState((prev) => ({
        ...prev,
        newPostContent: "",
      }));
    } catch (error) {
      console.error("Error creating post:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to create post. Please try again.",
      }));
    }
  };

  const replyToPost = async (postId: string, content: string) => {
    if (!content.trim()) return;

    try {
      // Determine if this is a reply to the main discussion or to another reply
      const isReplyToMainDiscussion = postId === discussionId;

      const requestBody = isReplyToMainDiscussion
        ? { content }
        : { content, parentReplyId: postId };

      await apiClient.post(
        `/elearning/discussions/${discussionId}/replies`,
        requestBody
      );

      // Reload the discussion to get updated posts
      await loadDiscussion();

      setState((prev) => ({
        ...prev,
        replyingTo: null,
        replyContent: "",
      }));
    } catch (error) {
      console.error("Error replying to post:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to post reply. Please try again.",
      }));
    }
  };

  const editPost = async (postId: string, content: string) => {
    try {
      // Determine if this is editing the main discussion or a reply
      const isEditingMainDiscussion = postId === discussionId;

      const endpoint = isEditingMainDiscussion
        ? `/elearning/discussions/${discussionId}`
        : `/elearning/replies/${postId}`;

      await apiClient.put(endpoint, {
        content,
      });

      // Reload the discussion to get updated content
      await loadDiscussion();
    } catch (error) {
      console.error("Error editing post:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to edit post. Please try again.",
      }));
    }
  };

  const deletePost = async (postId: string) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      // Determine if this is deleting the main discussion or a reply
      const isDeletingMainDiscussion = postId === discussionId;

      const endpoint = isDeletingMainDiscussion
        ? `/elearning/discussions/${discussionId}`
        : `/elearning/replies/${postId}`;

      await apiClient.delete(endpoint);

      if (isDeletingMainDiscussion) {
        // If deleting the main discussion, navigate back
        navigate("/elearning");
      } else {
        // Reload the discussion to remove the deleted reply
        await loadDiscussion();
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to delete post. Please try again.",
      }));
    }
  };

  const togglePin = async (postId: string) => {
    try {
      // Only the main discussion can be pinned
      if (postId !== discussionId) {
        alert("Only the main discussion can be pinned.");
        return;
      }

      await apiClient.post(`/elearning/discussions/${discussionId}/pin`);

      // Reload the discussion to get updated pin status
      await loadDiscussion();
    } catch (error) {
      console.error("Error toggling pin:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to toggle pin. Please try again.",
      }));
    }
  };

  const likePost = async (postId: string) => {
    try {
      // Determine if this is liking the main discussion or a reply
      const isLikingMainDiscussion = postId === discussionId;

      const endpoint = isLikingMainDiscussion
        ? `/elearning/discussions/${discussionId}/vote`
        : `/elearning/replies/${postId}/vote`;

      const voteType = isLikingMainDiscussion ? "up" : "like";

      await apiClient.post(endpoint, {
        voteType,
      });

      // Reload the discussion to get updated vote counts
      await loadDiscussion();
    } catch (error) {
      console.error("Error liking post:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to like post. Please try again.",
      }));
    }
  };

  const reportPost = async (_postId: string) => {
    alert(
      "Reporting functionality is not yet implemented. Please contact a teacher or administrator if you need to report inappropriate content."
    );
  };

  const filteredPosts = state.posts
    .filter((post) => {
      if (state.showPinnedOnly && !post.isPinned) return false;
      if (state.searchQuery) {
        return (
          post.content
            .toLowerCase()
            .includes(state.searchQuery.toLowerCase()) ||
          post.authorName
            ?.toLowerCase()
            .includes(state.searchQuery.toLowerCase())
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (state.sortBy) {
        case "oldest":
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        case "popular":
          return (b.likes || 0) - (a.likes || 0);
        default: // newest
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
      }
    });

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading discussion...</p>
        </div>
      </div>
    );
  }

  if (state.error || !state.discussion) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Discussion
          </h2>
          <p className="text-gray-600">{state.error}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Required
          </h2>
          <p className="text-gray-600">
            Please log in to view this discussion.
          </p>
        </div>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading discussion...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Discussion
          </h2>
          <p className="text-gray-600 mb-4">{state.error}</p>
          <button
            onClick={loadDiscussion}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!state.discussion) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Discussion Not Found
          </h2>
          <p className="text-gray-600">
            The discussion you're looking for doesn't exist or you don't have
            access to it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {state.discussion.title}
                </h1>
                {state.discussion.status === "closed" && (
                  <Lock className="w-5 h-5 text-gray-500" />
                )}
              </div>

              <p className="text-gray-600 mb-4">
                {state.discussion.description}
              </p>

              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <MessageSquare className="w-4 h-4" />
                  <span>{state.posts.length} posts</span>
                </div>

                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4" />
                  <span>{onlineUsers.length} online</span>
                </div>
              </div>
            </div>

            {isTeacher && (
              <button
                onClick={() => {
                  // Toggle discussion lock
                }}
                className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {state.discussion.status === "closed" ? (
                  <Unlock className="w-4 h-4" />
                ) : (
                  <Lock className="w-4 h-4" />
                )}
                <span>
                  {state.discussion.status === "closed" ? "Unlock" : "Lock"}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search posts..."
                value={state.searchQuery}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, searchQuery: e.target.value }))
                }
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <select
                value={state.sortBy}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    sortBy: e.target.value as any,
                  }))
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="popular">Most Popular</option>
              </select>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={state.showPinnedOnly}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      showPinnedOnly: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Pinned only</span>
              </label>
            </div>
          </div>
        </div>

        {/* New Post */}
        {state.discussion.status !== "closed" && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900">Add a reply</h3>
              <button
                onClick={() =>
                  state.discussion &&
                  navigate(
                    `/elearning/courses/${state.discussion.courseId}/discussions`
                  )
                }
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Start New Discussion
              </button>
            </div>

            <textarea
              value={state.newPostContent}
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  newPostContent: e.target.value,
                }));
                handleTyping();
              }}
              placeholder="Share your thoughts..."
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <div className="flex justify-end mt-3">
              <button
                onClick={createPost}
                disabled={!state.newPostContent.trim()}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                <span>Post</span>
              </button>
            </div>
          </div>
        )}

        {/* Typing Indicator */}
        <UserTypingIndicator typingUsers={typingUsers} />

        {/* Posts */}
        <div className="space-y-6">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No posts yet
              </h3>
              <p className="text-gray-600">
                {state.discussion.status === "closed"
                  ? "This discussion is locked."
                  : "Be the first to start the discussion!"}
              </p>
            </div>
          ) : (
            filteredPosts.map((post) => (
              <PostComponent
                key={post._id}
                post={post}
                onReply={(postId) =>
                  setState((prev) => ({ ...prev, replyingTo: postId }))
                }
                onLike={likePost}
                onEdit={editPost}
                onDelete={deletePost}
                onPin={togglePin}
                onReport={reportPost}
                currentUserId={currentUserId}
                isTeacher={isTeacher}
              />
            ))
          )}
        </div>

        {/* Reply Modal */}
        {state.replyingTo && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                Reply to Post
              </h3>

              <textarea
                value={state.replyContent}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    replyContent: e.target.value,
                  }))
                }
                placeholder="Write your reply..."
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />

              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      replyingTo: null,
                      replyContent: "",
                    }))
                  }
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    replyToPost(state.replyingTo!, state.replyContent)
                  }
                  disabled={!state.replyContent.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscussionBoard;
